import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { hasAnyAccessToResource } from "api/general";
import { FormSection } from "components/FormControls/FormSection";
import { ItemTypeCheckbox } from "components/FormControls/ItemTypeCheckbox";
import { HelpPanel } from "components/HelpPanel/HelpPanel";
import { ReactComponent as Lock03Icon } from "components/Icons/lock-03.svg";
import { defaultOLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { kebabCase } from "lodash";
import { memo } from "react";
import { DefaultLanguageDataState } from "reduxState/translations/translationsSlice";
import {
  CampaignTriggers,
  CampaignTriggersUnion,
  CodeGeneratorCharacterSet,
} from "types/campaigns";
import { useStyles } from "../../../Common/Campaign.styles";
import { campaignTriggers } from "../../../Common/campaignHelpers";
import { Achievement } from "./Achievement/Achievement";
import { ExternalEvent } from "./ExternalEvent/ExternalEvent";
import { InternalEvent } from "./InternalEvent/InternalEvent";
import { RedemptionCode } from "./RedemptionCode/RedemptionCode";

interface Props {
  trigger: CampaignTriggersUnion;
  event?: string;
  achievementId?: string;
  isEditMode: boolean;
  setFieldsValue: (
    fields: React.SetStateAction<{
      event?: string;
      trigger: CampaignTriggersUnion;
      rules: [];
      multiLevel?: number;
      eventCodeAttribute?: string;
      generateCodes?: number;
      codeGenerator?: {
        characterSet: CodeGeneratorCharacterSet;
        length: number;
        prefix?: string;
      };
      achievementId?: string;
    }>,
  ) => void;
  defaultLang: DefaultLanguageDataState;
  title?: string;
  errors: any;
}

export const Triggers = memo(
  ({
    trigger,
    isEditMode,
    event,
    achievementId,
    setFieldsValue,
    defaultLang,
    errors,
    title,
  }: Props) => {
    const classes = useStyles();
    const { message } = useMessage();
    const hasAnyCustomEventPermission = hasAnyAccessToResource("CUSTOM_EVENT");
    const isInternalEventTrigger = trigger === CampaignTriggers.INTERNAL_EVENT;
    const isExternalEventTrigger = trigger === CampaignTriggers.CUSTOM_EVENT;
    const isAchievementTrigger = trigger === CampaignTriggers.ACHIEVEMENT;
    const isRedemptionCodeTrigger =
      trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE;

    return (
      <Grid container spacing={12}>
        <Grid size={{ xs: 8 }}>
          <FormSection
            title={message({ id: title || "selectCampaignTrigger" })}
            subTitle={
              isEditMode ? (
                <Box className={classes.disabledCampaignInfoWrapper}>
                  <Lock03Icon className={classes.lockIcon} />
                  <Typography
                    fontSize="14px"
                    color={defaultOLTheme.orange600}
                    lineHeight="20px"
                  >
                    {message(
                      {
                        id: "disabledCampaignTypeTriggerInfo",
                      },
                      {
                        type: message({
                          id: "campaign",
                        }).toLowerCase(),
                      },
                    )}
                  </Typography>
                </Box>
              ) : null
            }
            sectionError={errors}
            noBorder
            noPadding
          >
            <Grid container spacing={12}>
              {campaignTriggers.map((campaignTrigger) => {
                return (
                  <Grid size={{ xs: 4 }} key={campaignTrigger.campaign}>
                    <ItemTypeCheckbox
                      activeItem={campaignTrigger.campaign === trigger}
                      label={message({ id: campaignTrigger.label })}
                      description={message({
                        id: campaignTrigger.descriptionText,
                      })}
                      data-test-id={`campaign-trigger-${kebabCase(
                        campaignTrigger.campaign,
                      )}`}
                      onClick={
                        // Clear necessary fields when trigger is changed
                        !isEditMode
                          ? () => {
                              setFieldsValue({
                                event: undefined,
                                trigger: campaignTrigger.campaign,
                                rules: [],
                                multiLevel: undefined,
                                eventCodeAttribute: undefined,
                                generateCodes: undefined,
                                codeGenerator: undefined,
                                achievementId: undefined,
                              });
                            }
                          : undefined
                      }
                      disabled={
                        isEditMode ||
                        (!hasAnyCustomEventPermission &&
                          campaignTrigger.campaign ===
                            CampaignTriggers.CUSTOM_EVENT)
                      }
                      tooltip={campaignTrigger.tooltip}
                    />
                  </Grid>
                );
              })}
            </Grid>
            {isInternalEventTrigger && (
              <InternalEvent isEditMode={isEditMode} />
            )}
            {isExternalEventTrigger && (
              <ExternalEvent event={event} isEditMode={isEditMode} />
            )}
            {isAchievementTrigger && (
              <Achievement
                achievementId={achievementId}
                defaultLang={defaultLang}
                isEditMode={isEditMode}
              />
            )}
            {isRedemptionCodeTrigger && (
              <RedemptionCode event={event} isEditMode={isEditMode} />
            )}
          </FormSection>
        </Grid>
        <Grid size={{ xs: 4 }} mt={20}>
          <HelpPanel
            title="campaignTriggers"
            description="campaignTriggerHelpDesc"
          />
        </Grid>
      </Grid>
    );
  },
);
